import {useEffect, useMemo, useState, type DragEvent, type RefObject} from 'react';
import type {PlayerRef} from '@remotion/player';
import {useAssets, useAllItems, useCurrentStateAsRef, useWriteContext} from '../../vendor/src/features/remotion-editor/editor/utils/use-context';
import {useAssetDropPreview} from '../../vendor/src/features/remotion-editor/editor/timeline/asset-drop-preview-provider';
import {generatedSourceAssets, prepareGeneratedSource, type GeneratedSourceAsset, type GeneratedSourceFilter} from './generated-source-feed';
import {setWorkAssetDrag, WORK_ASSET_DRAG_TYPE} from './work-asset-library';
import {captureInsertionTarget} from './incoming-media';
import {commitIncomingMedia} from './commit-incoming-media';
import {WorkMediaPreview} from './WorkMediaPreview';

export function GeneratedSourceFeed({playerRef, filter}: {playerRef: RefObject<PlayerRef | null>; filter: GeneratedSourceFilter}) {
  const {assets} = useAssets();
  const {items} = useAllItems();
  const current = useCurrentStateAsRef();
  const write = useWriteContext();
  const {setPreviewState} = useAssetDropPreview();
  const [message, setMessage] = useState('');
  const rows = useMemo(() => generatedSourceAssets({assets, items}, filter), [assets, items, filter]);
  const clearDrag = () => {setWorkAssetDrag(null); setPreviewState(null);};
  useEffect(() => () => {setWorkAssetDrag(null); setPreviewState(null);}, [setPreviewState]);
  const add = (asset: GeneratedSourceAsset) => {
    try {
      const document = current.current.undoableState;
      const prepared = prepareGeneratedSource(document, asset.id, crypto.randomUUID(), playerRef.current?.getCurrentFrame() ?? 0);
      commitIncomingMedia(write, prepared.item, prepared.asset, captureInsertionTarget(document.tracks), document.fps);
      setMessage(`${asset.filename} 원본을 추가했습니다. 실행 취소로 되돌릴 수 있습니다.`);
    } catch (error) {setMessage(error instanceof Error ? error.message : '생성 원본을 추가하지 못했습니다.');}
  };
  const drag = (event: DragEvent, asset: GeneratedSourceAsset) => {
    try {
      const document = current.current.undoableState;
      const prepared = prepareGeneratedSource(document, asset.id, crypto.randomUUID(), 0);
      setWorkAssetDrag({assetId: asset.id, document, name: asset.filename,
        duration: prepared.item.durationInFrames / document.fps * 1000,
        durationInFrames: prepared.item.durationInFrames, contentType: prepared.asset.type === 'video' ? 1 : 3});
      event.dataTransfer.setData(WORK_ASSET_DRAG_TYPE, asset.id);
      event.dataTransfer.effectAllowed = 'copy';
    } catch (error) {
      event.preventDefault(); clearDrag();
      setMessage(error instanceof Error ? error.message : '생성 원본을 다시 선택해 주세요.');
    }
  };
  return <section aria-label="현재 작업 생성 소스" className="flex w-full min-w-0 flex-col gap-2">
    <p className="text-xs text-neutral-400">정밀 편집에서 생성한 영상과 음성입니다. 이전 제작 원본은 소스에서 확인할 수 있습니다.</p>
    <p role="status" className="text-xs">{message}</p>
    {rows.length ? <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2xs">
      {rows.map(({asset, placements,receipt}) => <article key={asset.id} draggable onDragStart={event => drag(event, asset)} onDragEnd={clearDrag} className="min-w-0 rounded border border-neutral-700 p-2">
        {filter === 'all' && <p className="text-xs text-neutral-400">{asset.type === 'video' ? '영상' : '음성'}</p>}
        {asset.remoteUrl ? <WorkMediaPreview type={asset.type} url={asset.remoteUrl} name={asset.filename} playerRef={playerRef}/> : <p className="text-xs">원본 주소를 확인해 주세요.</p>}
        <p className="truncate text-xs" title={asset.filename}>{asset.filename}</p>
        <p className="text-xs text-neutral-400">{placements ? `${placements}곳에 사용 중` : '미사용'}</p>
        {receipt&&(receipt.actualModel||receipt.requestedModel||receipt.provider)&&<p className="text-xs text-neutral-400">{[receipt.actualModel?`사용 모델: ${receipt.actualModel}`:receipt.requestedModel?`요청 모델: ${receipt.requestedModel}`:'',receipt.provider?(receipt.provider==='sedeo'?'Seedance':'Kling'):''].filter(Boolean).join(' · ')}</p>}
        <button type="button" onClick={() => add(asset)} aria-label={`${asset.filename} 타임라인에 추가`} className="mt-2 w-full rounded border border-neutral-600 p-1 text-xs">추가</button>
      </article>)}
    </div> : <p className="text-sys-cont-neutral-tertiary text-body-small py-6 text-center">{filter === 'video' ? '아직 정밀 편집에서 생성한 영상이 없습니다.' : filter === 'audio' ? '아직 정밀 편집에서 생성한 음성이 없습니다.' : '아직 정밀 편집에서 생성한 영상이나 음성이 없습니다.'}</p>}
  </section>;
}
